// Запись одной реплики с авто-остановкой по тишине (VAD), для серверного
// распознавания (Whisper). Поведение как у одноразового SpeechRecognizer:
// start() записывает одну фразу, на паузе в речи отдаёт blob через onResult.
// Управление циклом (перезапуск, пауза на время ответа агента) — у вызывающего.

export interface WhisperRecorderOptions {
  /** Готовая запись фразы → отправить на распознавание. */
  onResult: (blob: Blob, format: string) => void
  /** Уровень сигнала 0..1 (для индикатора и barge-in). */
  onLevel?: (level: number) => void
  /** Запись остановилась (можно начинать следующую). */
  onEnd?: () => void
  onError?: (msg: string) => void
  silenceMs?: number
  maxMs?: number
}

export class WhisperRecorder {
  private opts: WhisperRecorderOptions
  private stream: MediaStream | null = null
  private ctx: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private rec: MediaRecorder | null = null
  private chunks: Blob[] = []
  private raf: number | null = null
  private running = false
  private speaking = false
  private discard = false
  private silenceStart = 0
  private startedAt = 0
  private mime = ''
  readonly supported: boolean

  constructor(opts: WhisperRecorderOptions) {
    this.opts = opts
    this.supported =
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined'
  }

  private async ensureStream(): Promise<void> {
    if (this.stream) return
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      video: false,
    })
    const Ctx: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.ctx = new Ctx()
    const src = this.ctx.createMediaStreamSource(this.stream)
    this.analyser = this.ctx.createAnalyser()
    this.analyser.fftSize = 512
    src.connect(this.analyser)
    this.mime = MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : ''
  }

  async start(): Promise<void> {
    if (!this.supported) return
    try {
      await this.ensureStream()
    } catch (e) {
      this.opts.onError?.(String((e as Error)?.message ?? e))
      return
    }
    if (!this.stream) return
    if (this.ctx?.state === 'suspended') await this.ctx.resume()

    this.chunks = []
    this.speaking = false
    this.discard = false
    this.silenceStart = 0
    this.startedAt = Date.now()
    this.running = true

    this.rec = this.mime ? new MediaRecorder(this.stream, { mimeType: this.mime }) : new MediaRecorder(this.stream)
    this.rec.ondataavailable = (e) => {
      if (e.data && e.data.size) this.chunks.push(e.data)
    }
    this.rec.onstop = () => {
      const fmt = (this.mime || 'audio/webm').includes('mp4') ? 'mp4' : 'webm'
      const blob = new Blob(this.chunks, { type: this.mime || 'audio/webm' })
      if (!this.discard && this.speaking && blob.size > 1500) this.opts.onResult(blob, fmt)
      this.opts.onEnd?.()
    }
    this.rec.start()
    this.monitor()
  }

  private monitor(): void {
    if (!this.running || !this.analyser) return
    const buf = new Uint8Array(this.analyser.fftSize)
    this.analyser.getByteTimeDomainData(buf)
    let sum = 0
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128
      sum += v * v
    }
    const rms = Math.sqrt(sum / buf.length)
    this.opts.onLevel?.(Math.min(1, rms * 4))

    const now = Date.now()
    const THRESH = 0.045
    if (rms > THRESH) {
      this.speaking = true
      this.silenceStart = 0
    } else if (this.speaking) {
      if (!this.silenceStart) this.silenceStart = now
      else if (now - this.silenceStart > (this.opts.silenceMs ?? 1100)) {
        this.finishRec()
        return
      }
    }
    if (now - this.startedAt > (this.opts.maxMs ?? 20000)) {
      this.finishRec()
      return
    }
    this.raf = requestAnimationFrame(() => this.monitor())
  }

  private finishRec(): void {
    this.running = false
    if (this.raf != null) cancelAnimationFrame(this.raf)
    this.raf = null
    try {
      if (this.rec && this.rec.state !== 'inactive') this.rec.stop()
    } catch {
      /* noop */
    }
  }

  /** Остановить текущую запись и отдать результат (если была речь). */
  stop(): void {
    this.discard = false
    this.finishRec()
  }

  /** Прервать без отправки (пауза на время ответа агента). */
  abort(): void {
    this.discard = true
    this.finishRec()
  }

  /** Полностью освободить микрофон. */
  dispose(): void {
    this.abort()
    this.opts.onLevel?.(0)
    try {
      this.stream?.getTracks().forEach((t) => t.stop())
    } catch {
      /* noop */
    }
    this.ctx?.close().catch(() => {})
    this.stream = null
    this.ctx = null
    this.analyser = null
  }
}
